import {
  UPDATE_ONBOARDING,
  ACTIVATE_MUTATION,
  CHANGE_EMAIL_MUTATION,
  CLEAR_SUBSCRIPTION_CACHE,
  CREATE_MEMBERSHIP,
  CREATE_ROLE_ASSIGNMENT,
  DELETE_ACCOUNT_MUTATION,
  DELETE_PROFILE_PICTURE,
  DELETE_ROLE_ASSIGNMENT,
  IMPERSONATE_USER_MUTATION,
  LOGIN_MUTATION,
  LOGIN_WITH_SSO_MUTATION,
  LOGOUT_MUTATION,
  REFRESH_TOKEN_MUTATION,
  REGISTER_MUTATION,
  REGISTER_SSO_MUTATION,
  REMOVE_MEMBERSHIP,
  REQUEST_ACTIVATION_LINK_MUTATION,
  REQUEST_FREE_ACCOUNT,
  REQUEST_GET_SCHOOL_APPROVAL_MUTATION,
  REQUEST_PASSWORD_RESET_MUTATION,
  RESET_PASSWORD_MUTATION,
  REVOKE_TOKEN_MUTATION,
  SEND_STUDENT_WELCOME_EMAILS,
  SEND_VERIFICATION_EMAILS,
  START_DEMO_MUTATION,
  SWITCH_ACTIVE_ROLE_ASSIGNMENT,
  UPDATE_PROFILE_PICTURE,
  UPDATE_ROLE_ASSIGNMENT,
  UPDATE_TEACHER_INFO_MUTATION,
  UPDATE_USER_PREFERENCES_MUTATION,
  UNSUBSCRIBE_FROM_MARKETING_MUTATION,
  UNSUBSCRIBE_FROM_MARKETING_LINK_MUTATION,
} from "./Mutations";
import {
  GET_ONBOARDING,
  ALL_USER_QUERY,
  EXISTING_REGISTRATION_DATA,
  GET_ALL_AVAILABLE_ROLES,
  GET_USER_DETAILS,
  GET_USER_PREFERENCES,
  USER_DASHBOARD_QUERY,
  USER_QUERY,
  USER_REFERRAL_DATA,
  MARKETING_UNSUBSCRIBE_TOKEN_STATUS_QUERY,
} from "./Queries";

export class AuthClient {
  constructor(client) {
    this.client = client;
  }

  async loginWithSso({ provider, token }) {
    const res = await this.client.mutate({
      mutation: LOGIN_WITH_SSO_MUTATION,
      variables: { provider, token },
    });
    return res.data.loginWithSso;
  }
  async login({ email, password }) {
    const res = await this.client.mutate({
      mutation: LOGIN_MUTATION,
      variables: { username: email, password },
    });
    return res.data.tokenAuth;
  }
  async requestGetSchoolApproval({ dpoEmail }) {
    const res = await this.client.mutate({
      mutation: REQUEST_GET_SCHOOL_APPROVAL_MUTATION,
      variables: { dpoEmail: dpoEmail },
    });
    return res.data.requestGetSchoolApproval;
  }
  async revokeToken(refreshToken) {
    // NOTE: This is currently used to track impersonation stops - if used for something else will need to rethink that logic
    const res = await this.client.mutate({
      mutation: REVOKE_TOKEN_MUTATION,
      variables: { refreshToken: refreshToken },
    });
    return res.data.revokeToken;
  }

  async logout() {
    const res = await this.client.mutate({
      mutation: LOGOUT_MUTATION,
    });
    return res.data;
  }

  async refreshToken(refreshToken) {
    const res = await this.client.mutate({
      mutation: REFRESH_TOKEN_MUTATION,
      variables: { refreshToken: refreshToken },
    });
    return res.data.refreshToken;
  }

  async requestPassword({ email }) {
    const res = await this.client.mutate({
      mutation: REQUEST_PASSWORD_RESET_MUTATION,
      variables: { email: email },
    });
    return res.data.requestPasswordReset;
  }

  async resetPassword({ uid, token, password }) {
    const res = await this.client.mutate({
      mutation: RESET_PASSWORD_MUTATION,
      variables: { uid, token, password },
    });
    return res.data.resetPassword;
  }
  async activate({ uid, token }) {
    const res = await this.client.mutate({
      mutation: ACTIVATE_MUTATION,
      variables: { uid, token },
    });
    return res.data.activateAccount;
  }

  async sendVerificationEmails({ users }) {
    const res = await this.client.mutate({
      mutation: SEND_VERIFICATION_EMAILS,
      variables: { users },
    });
    return res.data.sendVerificationEmails;
  }

  async sendStudentWelcomeEmails({ users, school }) {
    const res = await this.client.mutate({
      mutation: SEND_STUDENT_WELCOME_EMAILS,
      variables: { users, school },
    });
    return res.data.sendStudentWelcomeEmail;
  }

  async register({
    firstName,
    lastName,
    email,
    password,
    school,
    termsAndConditions,
  }) {
    const res = await this.client.mutate({
      mutation: REGISTER_MUTATION,
      variables: {
        firstName,
        lastName,
        email,
        password,
        school,
        termsAndConditions,
      },
    });
    return res.data.register;
  }
  async startDemo({ firstName, lastName, email, subject, yearGroup, school }) {
    const res = await this.client.mutate({
      mutation: START_DEMO_MUTATION,
      variables: {
        firstName,
        lastName,
        email,
        subject,
        yearGroup,
        school,
      },
    });
    return res.data.startDemo;
  }
  async requestFreeAccount({
    firstName,
    lastName,
    email,
    dataOfficerEmail,
    referralCode,
    subject,
    yearGroups,
    school,
  }) {
    const res = await this.client.mutate({
      mutation: REQUEST_FREE_ACCOUNT,
      variables: {
        firstName,
        lastName,
        email,
        dataOfficerEmail,
        referralCode,
        subject,
        yearGroups,
        school,
      },
    });
    return res.data.requestFreeAccount;
  }
  async registerWithSSO({ school, termsAndConditions, provider, token }) {
    const res = await this.client.mutate({
      mutation: REGISTER_SSO_MUTATION,
      variables: {
        provider,
        token,
        school,
        termsAndConditions,
      },
    });
    return res.data.registerSso;
  }
  async requestActivationLink({ email }) {
    const res = await this.client.mutate({
      mutation: REQUEST_ACTIVATION_LINK_MUTATION,
      variables: {
        email,
      },
    });
    return res.data.requestActivationCode;
  }

  async updateTeacherInfo({ school, subject = null }) {
    const res = await this.client.mutate({
      mutation: UPDATE_TEACHER_INFO_MUTATION,
      variables: {
        school,
        subject,
      },
    });
    return res.data.updateTeacherInfo;
  }

  async deleteAccount() {
    const res = await this.client.mutate({
      mutation: DELETE_ACCOUNT_MUTATION,
    });
    return res.data.deleteAccount;
  }

  async changeEmail({ email }) {
    const res = await this.client.mutate({
      mutation: CHANGE_EMAIL_MUTATION,
      variables: {
        email,
      },
    });
    return res.data.changeEmail;
  }
  async impersonateUser({ targetUser }) {
    const res = await this.client.mutate({
      mutation: IMPERSONATE_USER_MUTATION,
      variables: {
        targetUser,
      },
    });
    return res.data.impersonateUser;
  }
  async clearSubscriptionCache() {
    const res = await this.client.mutate({
      mutation: CLEAR_SUBSCRIPTION_CACHE,
    });
    return res.data.clearSubscriptionCache;
  }

  async me() {
    const res = await this.client.query({
      query: USER_QUERY,
      fetchPolicy: "network-only",
    });
    return res.data.user;
  }

  async dashboardStats() {
    const res = await this.client.query({
      query: USER_DASHBOARD_QUERY,
    });
    return res.data.dashboardData;
  }

  async fetchUserReferralData() {
    const res = await this.client.query({
      query: USER_REFERRAL_DATA,
    });
    return res.data.referralStatus;
  }

  async allUsers({
    searchString,
    role = "",
    school = null,
    schoolGroup = null,
    subscriptionStatus = "",
    emailStatus = "",
    pseudonymisationStatus = "active",
    first = 20,
    after = null,
  }) {
    const res = await this.client.query({
      query: ALL_USER_QUERY,
      variables: {
        searchString,
        school,
        schoolGroup,
        role,
        subscriptionStatus,
        emailStatus,
        pseudonymisationStatus,
        first,
        after,
      },
      fetchPolicy: "network-only",
    });
    return res.data.users;
  }

  async requestExistingRegistrationData() {
    const res = await this.client.query({
      query: EXISTING_REGISTRATION_DATA,
    });
    return res.data.existingRegistrationData;
  }
  async getOnboarding() {
    const res = await this.client.query({
      query: GET_ONBOARDING,
      fetchPolicy: "no-cache",
    });
    return res.data.currentUserOnboarding;
  }

  async updateOnboarding(variables) {
    const res = await this.client.mutate({
      mutation: UPDATE_ONBOARDING,
      variables,
    });
    return res.data.updateOnboarding.state;
  }

  async getUserPreferences() {
    const res = await this.client.query({
      query: GET_USER_PREFERENCES,
    });
    return res.data.userPreferences;
  }
  async getUserDetails({ user }) {
    const res = await this.client.query({
      query: GET_USER_DETAILS,
      variables: { user: user },
    });
    return res.data;
  }

  async getAllAvailableRoles() {
    const res = await this.client.query({
      query: GET_ALL_AVAILABLE_ROLES,
    });
    return res.data.allAvailableRoles;
  }

  async updateUserPreferences(prefs) {
    const res = await this.client.mutate({
      mutation: UPDATE_USER_PREFERENCES_MUTATION,
      variables: {
        prefs,
      },
    });
    return res.data.updateUserPreferences;
  }

  async unsubscribeFromMarketing({ reason, detail }) {
    const res = await this.client.mutate({
      mutation: UNSUBSCRIBE_FROM_MARKETING_MUTATION,
      variables: { reason: reason || null, detail },
    });
    return res.data.unsubscribeFromMarketing;
  }

  async getMarketingUnsubscribeTokenStatus({ uid, token }) {
    const res = await this.client.query({
      query: MARKETING_UNSUBSCRIBE_TOKEN_STATUS_QUERY,
      variables: { uid, token },
      fetchPolicy: "no-cache",
    });
    return res.data.marketingUnsubscribeTokenStatus;
  }

  async unsubscribeFromMarketingLink({ uid, token, reason, detail }) {
    const res = await this.client.mutate({
      mutation: UNSUBSCRIBE_FROM_MARKETING_LINK_MUTATION,
      variables: { uid, token, reason: reason || null, detail },
    });
    return res.data.unsubscribeFromMarketingLink;
  }

  async updateProfilePicture({ image }) {
    const res = await this.client.mutate({
      mutation: UPDATE_PROFILE_PICTURE,
      variables: { image },
      context: { hasUpload: true },
    });
    return res.data.updateProfilePicture;
  }

  async deleteProfilePicture() {
    const res = await this.client.mutate({
      mutation: DELETE_PROFILE_PICTURE,
    });
    return res.data.deleteProfilePicture;
  }
  async switchActiveRoleAssignment({ roleAssignment }) {
    const res = await this.client.mutate({
      mutation: SWITCH_ACTIVE_ROLE_ASSIGNMENT,
      variables: { roleAssignment },
    });
    return res.data.switchActiveRoleAssignment;
  }
  async deleteRoleAssignment({ roleAssignment }) {
    const res = await this.client.mutate({
      mutation: DELETE_ROLE_ASSIGNMENT,
      variables: { roleAssignment },
    });
    return res.data.deleteRoleAssignment;
  }
  async updateRoleAssignment({ roleAssignment, subjects, educationLevels }) {
    const res = await this.client.mutate({
      mutation: UPDATE_ROLE_ASSIGNMENT,
      variables: { roleAssignment, subjects, educationLevels },
    });
    return res.data.updateRoleAssignment;
  }
  async removeMembership({ schoolGroupMembership, schoolMembership }) {
    const res = await this.client.mutate({
      mutation: REMOVE_MEMBERSHIP,
      variables: { schoolGroupMembership, schoolMembership },
    });
    return res.data.removeMembership;
  }
  async createMembership({ user, schoolGroup, school, allowNonSchoolEmail }) {
    const res = await this.client.mutate({
      mutation: CREATE_MEMBERSHIP,
      variables: { user, schoolGroup, school, allowNonSchoolEmail },
    });
    return res.data.createMembership;
  }
  async createRoleAssignment({
    role,
    schoolGroupMembership,
    schoolMembership,
  }) {
    const res = await this.client.mutate({
      mutation: CREATE_ROLE_ASSIGNMENT,
      variables: { role, schoolGroupMembership, schoolMembership },
    });
    return res.data.createRoleAssignment;
  }
}
